import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './CategoryGrid.css';
import { useData } from '../context/DataContext';
import { useStorefrontSettings } from '../context/StorefrontSettingsContext';
import { formatCatalogDisplayName, sortCategoriesAlphabetically } from '../utils/categoryMenuTree';
import { trackCategoryClick } from '../utils/analyticsTracker';
import { pickMediaUrl } from '../utils/mediaUrl';

function slugify(str) {
  return String(str || '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

const categoryPlaceholders = {
  electronics: '/images/categories/electronics.jpg',
  fashion: '/images/categories/fashion.jpg',
  home: '/images/categories/home.jpg',
  sports: '/images/categories/sports.jpg',
  beauty: '/images/categories/beauty.jpg',
  toys: '/images/categories/toys.jpg'
};

const SKELETON_COUNT = 6;
const CAROUSEL_AUTO_MS = 4500;

const CategoryGrid = ({ onCategorySelect }) => {
  const { categoriesForGrid, categories, loading } = useData();
  const { categoryGridLayout, loaded: settingsLoaded, imagekitPrimaryEnabled } =
    useStorefrontSettings();
  const navigate = useNavigate();
  const trackRef = useRef(null);
  const pauseAutoRef = useRef(false);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);

  const isCarousel = settingsLoaded && categoryGridLayout === 'carousel';
  const showSkeleton =
    categoriesForGrid.length === 0 && categories.length === 0 && loading;

  const fallbackCategories = [
    { id: 'furniture', name: 'Furniture', slug: 'furniture' },
    { id: 'health-beauty', name: 'Health & Beauty', slug: 'health-beauty' },
    { id: 'home-appliances', name: 'Home Appliances', slug: 'home-appliances' },
    { id: 'kitchen-dining', name: 'Kitchen & Dining', slug: 'kitchen-dining' },
    { id: 'home-essentials-decor', name: 'Home Essentials & Décor', slug: 'home-essentials-decor' },
    { id: 'lighting', name: 'Lighting', slug: 'lighting' },
    { id: 'tech-accessories', name: 'Tech & Accessories', slug: 'tech-accessories' },
    { id: 'sports-fitness', name: 'Sports & Fitness', slug: 'sports-fitness' }
  ];

  let displayCategories = [];
  if (!showSkeleton) {
    if (categoriesForGrid && categoriesForGrid.length > 0) {
      displayCategories = categoriesForGrid;
    } else if (categories.length > 0) {
      displayCategories = sortCategoriesAlphabetically(
        categories.filter((cat) => cat.showInCategoryGrid === true)
      );
    }
    if (displayCategories.length === 0 && !showSkeleton) {
      displayCategories = fallbackCategories;
    }
  }

  const handleCategoryClick = useCallback(
    (category) => {
      const categoryId = category._id || category.id;
      const idStr = String(categoryId);
      const slug = category.slug || slugify(category.name);
      trackCategoryClick(category);
      navigate(`/category/${slug}`);
      if (onCategorySelect) onCategorySelect(idStr);
    },
    [navigate, onCategorySelect]
  );

  const updateScrollButtons = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
    const overflow = maxScroll > 4;
    setHasOverflow(overflow);
    setCanScrollPrev(overflow && el.scrollLeft > 4);
    setCanScrollNext(overflow && el.scrollLeft < maxScroll - 4);
  }, []);

  const getScrollStep = useCallback(() => {
    const el = trackRef.current;
    if (!el) return 160;
    const card = el.querySelector('.category-card');
    if (!card) return 160;
    const gap = parseFloat(getComputedStyle(el).columnGap || getComputedStyle(el).gap || '20') || 20;
    return card.offsetWidth + gap;
  }, []);

  const scrollCarousel = useCallback(
    (direction) => {
      const el = trackRef.current;
      if (!el) return;
      const step = getScrollStep() * direction;
      const maxScroll = el.scrollWidth - el.clientWidth;
      let next = el.scrollLeft + step;
      if (next < 0) next = 0;
      if (next > maxScroll) next = maxScroll;
      el.scrollTo({ left: next, behavior: 'smooth' });
    },
    [getScrollStep]
  );

  const scrollCarouselNext = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    if (el.scrollLeft >= maxScroll - 4) {
      el.scrollTo({ left: 0, behavior: 'smooth' });
    } else {
      scrollCarousel(1);
    }
  }, [scrollCarousel]);

  useEffect(() => {
    if (!isCarousel || showSkeleton || displayCategories.length <= 1 || !hasOverflow) return undefined;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return undefined;

    const timer = setInterval(() => {
      if (pauseAutoRef.current) return;
      scrollCarouselNext();
    }, CAROUSEL_AUTO_MS);

    return () => clearInterval(timer);
  }, [isCarousel, showSkeleton, displayCategories.length, scrollCarouselNext, hasOverflow]);

  useEffect(() => {
    if (!isCarousel) return undefined;
    const el = trackRef.current;
    if (!el) return undefined;
    updateScrollButtons();
    el.addEventListener('scroll', updateScrollButtons, { passive: true });
    window.addEventListener('resize', updateScrollButtons);

    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => updateScrollButtons());
      ro.observe(el);
      const track = el.querySelector('.category-carousel-track');
      if (track) ro.observe(track);
    }

    const rafId = requestAnimationFrame(() => updateScrollButtons());

    return () => {
      cancelAnimationFrame(rafId);
      el.removeEventListener('scroll', updateScrollButtons);
      window.removeEventListener('resize', updateScrollButtons);
      if (ro) ro.disconnect();
    };
  }, [isCarousel, displayCategories.length, showSkeleton, updateScrollButtons]);

  const renderCategoryCard = (category) => {
    const categoryId = category._id || category.id;
    const categorySlug = category.slug || category.id;
    return (
      <div
        key={categoryId}
        className="category-card"
        onClick={() => handleCategoryClick(category)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleCategoryClick(category);
          }
        }}
        role="button"
        tabIndex={0}
      >
        <div className="category-image-container">
          <img
            src={
              pickMediaUrl({
                primary: category.image,
                imagekit: category.imageImagekit,
                preferImageKit: imagekitPrimaryEnabled,
              }) ||
              categoryPlaceholders[categorySlug] ||
              categoryPlaceholders.electronics
            }
            alt={category.name}
            className="category-image"
            loading="lazy"
            onError={(e) => {
              const primary = category.image || '';
              const ik = category.imageImagekit || '';
              const src = e.currentTarget.src || '';
              if (imagekitPrimaryEnabled && primary && !src.includes(primary.split('?')[0])) {
                e.currentTarget.src = primary;
                return;
              }
              if (!imagekitPrimaryEnabled && ik && !/ik\.imagekit\.io/i.test(src)) {
                e.currentTarget.src = ik;
                return;
              }
              e.currentTarget.src =
                categoryPlaceholders[categorySlug] || categoryPlaceholders.electronics;
            }}
          />
        </div>
        <h3 className="category-name">{formatCatalogDisplayName(category.name)}</h3>
      </div>
    );
  };

  const renderSkeletonCard = (key) => (
    <div key={key} className="category-card category-card--skeleton" aria-hidden>
      <div className="category-skeleton-image" />
      <div className="category-skeleton-name" />
    </div>
  );

  if (showSkeleton) {
    return (
      <section className="category-section" id="categories">
        <div className="category-container">
          <h2 className="category-section-title">Shop by Category</h2>
          {isCarousel ? (
            <div className="category-carousel category-carousel--skeleton" aria-busy="true" aria-label="Loading categories">
              <div className="category-carousel-btn category-carousel-btn--prev" aria-hidden tabIndex={-1} />
              <div className="category-carousel-viewport">
                <div className="category-carousel-track">
                  {Array.from({ length: SKELETON_COUNT }).map((_, i) => renderSkeletonCard(i))}
                </div>
              </div>
              <div className="category-carousel-btn category-carousel-btn--next" aria-hidden tabIndex={-1} />
            </div>
          ) : (
            <div className="category-grid category-grid--skeleton" aria-busy="true" aria-label="Loading categories">
              {Array.from({ length: SKELETON_COUNT }).map((_, i) => renderSkeletonCard(i))}
            </div>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className="category-section" id="categories">
      <div className="category-container">
        <h2 className="category-section-title">Shop by Category</h2>
        {displayCategories.length > 0 ? (
          isCarousel ? (
            <div
              className={`category-carousel${hasOverflow ? '' : ' category-carousel--fits'}`}
              onMouseEnter={() => { pauseAutoRef.current = true; }}
              onMouseLeave={() => { pauseAutoRef.current = false; }}
            >
              {hasOverflow ? (
                <button
                  type="button"
                  className="category-carousel-btn category-carousel-btn--prev"
                  onClick={() => scrollCarousel(-1)}
                  disabled={!canScrollPrev}
                  aria-label="Previous categories"
                >
                  ‹
                </button>
              ) : null}
              <div
                className="category-carousel-viewport"
                ref={trackRef}
              >
                <div className="category-carousel-track">
                  {displayCategories.map((category) => renderCategoryCard(category))}
                </div>
              </div>
              {hasOverflow ? (
                <button
                  type="button"
                  className="category-carousel-btn category-carousel-btn--next"
                  onClick={() => scrollCarousel(1)}
                  disabled={!canScrollNext}
                  aria-label="Next categories"
                >
                  ›
                </button>
              ) : null}
            </div>
          ) : (
            <div className="category-grid">
              {displayCategories.map((category) => renderCategoryCard(category))}
            </div>
          )
        ) : (
          <div className="no-categories">
            <p>No categories available. Please enable categories in the admin panel.</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default CategoryGrid;
